import { CreatePromptModelRequest, ModelType, UnprocessableEntityErrorResponse } from 'api/model';
import { getSchema, uiSchema } from 'api/prompts-models/formSchema';
import useModel from 'api/prompts-models/useModel';
import useSecrets from 'api/secrets/useSecrets';
import { isAxiosError } from 'axios';
import Loader from 'components/Loader';
import FormBlock from 'components/form/FormBlock';
import FormButtons from 'components/form/FormButtons';
import { SettingsFormShell } from 'components/settingsHub/SettingsFormShell';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

const FORM_ID = 'model-edit-form';

type Props = {
  onClose: () => void;
  modelId: string;
};

const ModelEdit = ({ onClose, modelId }: Props) => {
  const { projectId } = useParams();

  const { secrets } = useSecrets(projectId, true);
  const { modelIsLoading, model, update } = useModel({ modelId });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ModelType>();
  const [selectedBaseUrl, setSelectedBaseUrl] = useState<string>();

  const modelMissing = !modelIsLoading && !model;
  const closed = useRef(false);

  useEffect(() => {
    if (!modelMissing || closed.current) return;
    closed.current = true;

    toast.error('Model does not exist');
    onClose();
  }, [modelMissing, onClose]);

  if (modelIsLoading) return <Loader />;

  if (!model) return null;

  const provider = selectedProvider ?? model.type;
  const baseUrl = selectedBaseUrl ?? (model.settings as { base_url?: string } | null)?.base_url ?? '';

  const handleChange = (formData: CreatePromptModelRequest) => {
    const newProvider = formData.type;
    if (newProvider !== provider) {
      setSelectedProvider(newProvider);
    }
    const newBaseUrl = (formData.settings as { base_url?: string } | null)?.base_url ?? '';
    if (newBaseUrl !== baseUrl) {
      setSelectedBaseUrl(newBaseUrl);
    }
  };

  const onSubmitHandle = async (formData: CreatePromptModelRequest) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    try {
      await update.mutateAsync(formData);
      onClose();
    } catch (error) {
      if (isAxiosError<UnprocessableEntityErrorResponse>(error)) {
        const errorMessage = error.response?.data?.detail?.[0].msg;
        if (errorMessage && errorMessage.length > 0) {
          toast.error(errorMessage);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsFormShell
      title="Edit Model"
      footer={<FormButtons formId={FORM_ID} onCancel={onClose} cancelBtnTxt="back to list" isLoading={isLoading} />}
    >
      <FormBlock
        isLoading={isLoading}
        formId={FORM_ID}
        showButtons={false}
        initialValues={model}
        schema={getSchema(secrets)}
        uiSchema={uiSchema}
        onSubmit={(formData) => void onSubmitHandle(formData)}
        onCancel={onClose}
        onChange={handleChange}
        formContext={{
          secrets,
          extra: { provider, base_url: baseUrl },
        }}
      ></FormBlock>
    </SettingsFormShell>
  );
};

export default ModelEdit;
